

















































































































































































import Dropdown, { DropdownItem } from '@/components/Dropdown.vue';
import Icon from '@/components/Icon.vue';
import Call from '@/components/queue/Call.vue';
import ModalAttachQueue from '@/components/agent/ModalAttachQueue.vue';
import ModalUpdateAgent from '@/components/agent/ModalUpdateAgent.vue';
import Tooltip from '@/components/Tooltip.vue';
import capitalize from '@/utils/text';
import idealName from '@/utils/ideal-name';
import { BridgeAgent, Tier, CallState } from '@/store/modules/agent';
import {
    AgentCustomStatus,
    AgentConduct,
    AgentStatus,
    AgentState,
} from '@/utils/agent';
import { TierState, State } from '@/utils/tier/state';
import { Vue, Component, Prop } from 'vue-property-decorator';
import Chronometer from '@/atomics/Chronometer.vue';
import { LocaleMessages } from 'vue-i18n';

@Component({
    components: {
        Dropdown,
        Icon,
        ModalAttachQueue,
        ModalUpdateAgent,
        Chronometer,
        Call,
        Tooltip,
    },
})
export default class Agent extends Vue {
    @Prop(Boolean) readonly moreDetail!: boolean;

    @Prop(Boolean) readonly ringing!: boolean;

    @Prop(Boolean) readonly connected!: boolean;

    @Prop(Boolean) readonly show!: boolean;

    @Prop(Boolean) readonly idleTimeHidden!: boolean;

    @Prop(String) readonly queue!: string;

    @Prop(String) readonly name!: string;

    @Prop(String) readonly memberUuid!: string;

    @Prop(Array) readonly queues!: Array<string>;

    @Prop({ type: String, default: null }) readonly currentDomain!: string | null;

    idealName = idealName;

    capitalize = capitalize;

    tierState: typeof TierState = TierState;

    private runningCallChronos: Set<string> = new Set<string>();

    private lastCallEndedAt = 0;

    get isDisconnected(): boolean {
        return !this.connected || !this.agent.connected;
    }

    updated() {
        if (this.lastCallEndedAt > 0 && Date.now() - this.lastCallEndedAt > 500) {
            this.lastCallEndedAt = 0;
            this.$forceUpdate();
        }
    }

    get shouldShowRingCall() {
        return this.memberUuid !== ''
            && this.connected
            && this.getQueueCalls.length === 0;
    }

    get dropdownItemsAgent(): Array<DropdownItem> {
        return [
            {
                name: this.$t('assign_queue'),
                action: () => {
                    (this.$refs.modalAttachQueue as ModalAttachQueue).toogleModal();
                },
            },
            {
                name: this.$t('edit_agent'),
                action: () => {
                    (this.$refs.modalUpdateAgent as ModalUpdateAgent).toogleModal();
                },
            },
            {
                name: this.$t('hangup_call'),
                action: () => {
                    const call = this.$store.getters.getFirstCallIdByAgent(this.name);
                    if (call !== '') {
                        this.$store.dispatch('hangupCall', { name: this.name, call });
                    }
                },
            },
            {
                name: this.$t('dettach_queue'),
                action: () => {
                    this.$store.dispatch('dettachQueue', { name: this.name, queue: this.queue });
                },
            },
            {
                name: this.agent.doNotDisturb
                    ? this.$t('do_available')
                    : this.$t('do_not_disturb'),
                action: () => {
                    const actionToMake = this.agent.doNotDisturb
                        ? 'disableAgentDoNotDisturb'
                        : 'enableAgentDoNotDisturb';
                    this.$store.dispatch(actionToMake, this.name);
                },
            },
            {
                name: TierState.isPaused(this.agentAsTierInQueue.state)
                    ? this.$t('agent.state.ready')
                    : this.$t('agent.state.absent'),
                action: () => {
                    this.$store.dispatch('updateAgentState', {
                        queueName: this.queue,
                        agentName: this.name,
                        state: TierState.isPaused(this.agentAsTierInQueue.state)
                            ? State.Ready
                            : State.Absent,
                    });
                },
            },
        ];
    }

    get agentAsTierInQueue(): Tier {
        return this.$store.getters.queueTiers(this.queue)
            .find((tier: Tier) => tier.agent === this.name);
    }

    get isInactive(): boolean {
        return (this.agent.queueCalls?.size ?? 0) <= 0
            && this.activeCallStatesGlobal.length <= 0
            && this.memberUuid === '';
    }

    get agentCustomStatus(): AgentCustomStatus {
        const agentConduct: AgentConduct = {
            connected: !this.isDisconnected && !this.agent.isLoading,
            active: !this.isInactive,
        };
        return AgentCustomStatus.fromPanel(
            this.agent.status,
            this.agent.state,
            agentConduct,
            this.agent.previousState,
        );
    }

    get customStatusTranslation(): string | LocaleMessages {
        if (this.isDisconnected) {
            return this.$t(this.agentCustomStatus.translation());
        }

        const isReceiving = AgentState.isReceiving(this.agent.state);

        if (isReceiving) {
            if (this.currentDomain && this.allowedDomains.includes(this.currentDomain)) {
                return `${this.$t('dnd')}: ${this.$t('agent.custom_status.agitated')}`;
            }

            return this.$t('agent.custom_status.agitated');
        }

        if (this.isInCallFromTimer || this.hasActiveCallStates) {
            return this.$t('agent.custom_status.busy');
        }

        if (this.agent.doNotDisturb) {
            return this.$t('dnd');
        }

        if (AgentStatus.isBusy(this.agent.status)) {
            return this.$t('agent.custom_status.busy');
        }

        if (AgentStatus.isAvailable(this.agent.status)) {
            return this.$t('agent.status.available');
        }

        return this.$t(this.agentCustomStatus.translation());
    }

    get agent() {
        return this.$store.getters.getAgentByName(this.name);
    }

    get memberCall() {
        return this.$store.getters.queueMemberCall(this.queue, this.memberUuid);
    }

    get agentName(): string {
        return this.$store.getters.agentName(this.agent.name);
    }

    get agentNameExtension(): string {
        return this.$store.getters.agentExtensionName(this.agent.name);
    }

    get tooltipName(): string {
        if (this.agentName !== this.agentNameExtension) {
            return `(${this.agentNameExtension}) ${this.agentName}`;
        }
        return this.agentName;
    }

    get getQueueCalls() {
        if (this.isDisconnected) {
            return [];
        }

        const calls = Array.from(this.agent.queueCalls?.values() ?? [])
            .filter((call) => (call as BridgeAgent).queue === this.queue);

        const uniqueCalls = new Map(calls.map((call) => [(call as BridgeAgent).call, call]));
        return Array.from(uniqueCalls.values());
    }

    get getCallStates() {
        if (this.isDisconnected) {
            return [];
        }

        return this.$store.getters.agentCallStates(this.name);
    }

    get activeCallStatesGlobal(): Array<CallState> {
        const callStates: Array<CallState> = Array.from(this.agent.callStates?.values() ?? []) as Array<CallState>;

        return callStates.filter((call: CallState) => call.state !== 'hangup'
            && call.state !== 'destroy'
            && call.state !== 'completed');
    }

    get isInCallFromTimer(): boolean {
        if (this.isDisconnected) {
            return false;
        }

        return this.runningCallChronos.size > 0;
    }

    get hasActiveCallStates(): boolean {
        if (this.isDisconnected) {
            return false;
        }

        const hasQueueCallsGlobal = (this.agent.queueCalls?.size ?? 0) > 0;
        if (hasQueueCallsGlobal) {
            return true;
        }

        const hasActiveCallStatesGlobal = this.activeCallStatesGlobal.length > 0;
        if (hasActiveCallStatesGlobal) {
            return true;
        }

        const isReceiving = AgentState.isReceiving(this.agent.state);

        if (isReceiving) {
            return false;
        }

        const activeCallStates = this.getCallStates.filter((call: CallState) => call.state !== 'hangup'
            && call.state !== 'destroy'
            && call.state !== 'completed');

        const hasActiveCallStates = activeCallStates.length > 0;
        const hasQueueCalls = this.getQueueCalls.length > 0;
        const hasActiveMemberCall = this.memberUuid !== '' && !isReceiving;

        const result = hasActiveCallStates || hasQueueCalls || hasActiveMemberCall;
        return result;
    }

    get forcedIconClass(): string {
        if (this.isDisconnected) {
            return this.agentCustomStatus.statusIconClass();
        }

        const hasActiveCalls = this.runningCallChronos.size > 0;
        const hasActiveCallsInStore = this.hasActiveCallStates;
        const isReceiving = AgentState.isReceiving(this.agent.state);

        if ((hasActiveCalls || hasActiveCallsInStore) && !isReceiving) {
            return 'text-red-500';
        }

        if (isReceiving) {
            return 'text-yellow-500';
        }

        if (this.agent.doNotDisturb) {
            return 'text-blue-500';
        }

        const defaultStatus = this.agentCustomStatus.statusIconClass();
        return defaultStatus;
    }

    get rootBgClass(): string {
        if (this.isDisconnected) {
            return 'bg-gray-100';
        }

        const isReceiving = AgentState.isReceiving(this.agent.state);
        if (isReceiving) {
            return 'bg-white';
        }
        if (this.isInCallFromTimer || this.hasActiveCallStates) return 'bg-white';
        return 'bg-white';
    }

    get shouldShowDndStatus(): boolean {
        if (this.isInCallFromTimer || this.hasActiveCallStates) {
            return false;
        }

        return !!this.agent
            && !this.isDisconnected
            && this.agent.doNotDisturb
            && this.agent.customStatusMessage
            && this.agent.customStatusMessage.length > 0;
    }

    get shouldShowInactiveIcon(): boolean {
        return (this.isInactive || this.isDisconnected)
            && this.getQueueCalls.length === 0
            && this.getCallStates.length === 0;
    }

    allowedDomains = [
        'donottouchonlydev.myflexpbx.com',
        'biomax3.myflexpbx.com',
        'biomax2.myflexpbx.com',
        'biomax1.myflexpbx.com',
    ];

    onCallChrono(payload: { id: string; running: boolean }) {
        if (this.isDisconnected) {
            this.runningCallChronos.clear();
            return;
        }

        if (!payload || !payload.id) return;

        if (payload.id === this.memberUuid) return;

        if (payload.running) {
            this.runningCallChronos.add(payload.id);

            this.$nextTick(() => {
                this.$forceUpdate();
            });
        } else {
            this.runningCallChronos.delete(payload.id);

            this.lastCallEndedAt = Date.now();

            this.$nextTick(() => {
                this.$forceUpdate();

                if (this.runningCallChronos.size === 0) {
                    setTimeout(() => {
                        this.$forceUpdate();

                        setTimeout(() => {
                            if (!this.hasActiveCallStates) {
                                this.$forceUpdate();
                            }
                        }, 200);
                    }, 100);
                }
            });
        }
    }
}
